import * as _ from 'lodash-es';
import Embed from 'quill/blots/embed';
import type { Parchment } from 'quill/core';
import React from 'react';
import { createPortal } from 'react-dom';
import type { Store, StoreUpdater } from './ReactBlockEmbed';
import { createStore } from './ReactBlockEmbed';
import Scroll from './Scroll';
import { BLOT_VALUE_ATTRIBUTE } from '../constants';
import type { RenderPortalArgs } from '../QuillEditor';

abstract class ReactEmbed<
  State extends { value: unknown } = { value: unknown },
> extends Embed {
  static create(value: unknown) {
    const node = super.create(value) as Element;
    this.setValue(node, value);
    return node;
  }

  static setValue(domNode: Element, value: unknown) {
    domNode.setAttribute(BLOT_VALUE_ATTRIBUTE, JSON.stringify(value));
  }

  static value(domNode: Element) {
    const value = domNode.getAttribute(BLOT_VALUE_ATTRIBUTE);
    try {
      return value ? JSON.parse(value) : null;
    } catch (err) {
      // ignore errors since DOM may be manipulated outside of Quill
    }
    return null;
  }

  id = _.uniqueId();
  store: Store<State>;

  constructor(
    scroll: Parchment.ScrollBlot,
    domNode: Node,
    initialState: State,
  ) {
    super(scroll, domNode);

    this.store = createStore(initialState);

    this.render = this.render.bind(this);
  }

  setValue(value: State['value']) {
    this.statics.setValue(this.domNode, value);
  }

  setState(updater: StoreUpdater<State>) {
    this.store.setState(updater);
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  abstract render(state: State, args: RenderPortalArgs): React.ReactNode;

  renderPortal(args: RenderPortalArgs) {
    const { Connect } = this.store;
    const key = `editor_${this.statics.blotName}_${this.id}`;

    return createPortal(
      <Connect>{(state) => this.render(state, args)}</Connect>,
      this.contentNode,

      // Hint to the reconciler otherwise the whole component will be torn down and re-created.
      key,
    );
  }
}

export default ReactEmbed;
